import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Lock, CreditCard, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatMoney } from "@/lib/api";

const Field = ({ label, id, children, required, className = "" }) => (
  <div className={className}>
    <Label htmlFor={id} className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <div className="mt-1">{children}</div>
  </div>
);

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "MI",
    zip_code: "",
    notes: "",
  });

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        full_name: `${user.first_name} ${user.last_name}`,
        email: user.email,
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!placedOrder && items.length === 0) navigate("/cart");
  }, [items, navigate, placedOrder]);

  const tax = subtotal * 0.06;
  const shipping = subtotal >= 75 ? 0 : 9.95;
  const total = subtotal + tax + shipping;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const placeOrder = async (e) => {
    e.preventDefault();
    if (placing) return;
    setPlacing(true);
    try {
      const res = await api.post("/orders", {
        items: items.map((i) => ({
          product_id: i.product_id,
          sku: i.sku,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          image: i.image,
        })),
        shipping: {
          full_name: form.full_name,
          email: form.email,
          phone: form.phone,
          address_line1: form.address_line1,
          address_line2: form.address_line2,
          city: form.city,
          state: form.state,
          zip_code: form.zip_code,
          country: "United States",
        },
        notes: form.notes,
      });
      setPlacedOrder(res.data);
      clear();
      toast.success("Order created! Complete payment with Square below.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to place order");
    } finally {
      setPlacing(false);
    }
  };

  // ORDER SUCCESS + SQUARE PLACEHOLDER
  if (placedOrder) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <Check className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="mt-6 text-3xl font-black text-slate-900">Order Placed!</h1>
          <p className="mt-2 text-slate-600">
            Order <span className="font-bold text-slate-900">{placedOrder.order_number}</span> has been created. Complete payment below to finalize.
          </p>
          <div className="mt-6 bg-slate-50 rounded-lg p-5 text-left">
            <div className="font-semibold text-slate-900 mb-3">Order Summary</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>{formatMoney(placedOrder.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Tax</span><span>{formatMoney(placedOrder.tax)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Shipping</span><span>{placedOrder.shipping_cost === 0 ? "FREE" : formatMoney(placedOrder.shipping_cost)}</span></div>
              <div className="border-t border-slate-200 my-2" />
              <div className="flex justify-between font-black text-lg"><span>Total Due</span><span className="text-[#0b3d75]">{formatMoney(placedOrder.total)}</span></div>
            </div>
          </div>

          <div className="mt-6 p-5 border-2 border-dashed border-slate-300 rounded-lg text-left bg-slate-50">
            <div className="flex items-center gap-2 text-slate-800 font-bold">
              <CreditCard className="w-5 h-5" /> Pay with Square
            </div>
            <p className="text-sm text-slate-600 mt-2">
              Your Square payment integration will connect here. This checkout is
              ready for Square Web Payments SDK. Once your Square merchant
              credentials are added, the card form will appear here.
            </p>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2 text-xs text-amber-900">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span><b>Placeholder:</b> This button simulates a successful Square payment. Replace with real Square SDK integration in production.</span>
            </div>
            <Button
              className="w-full mt-4 bg-gradient-to-r from-slate-900 to-black hover:from-slate-800 text-white h-12 font-bold"
              onClick={() => {
                toast.success("Simulated payment complete!");
                navigate(`/order/${placedOrder.order_number}`);
              }}
            >
              <Lock className="w-4 h-4 mr-2" /> Pay {formatMoney(placedOrder.total)} with Square
            </Button>
          </div>

          <div className="mt-6">
            <Link to="/shop" className="text-[#0b3d75] font-semibold hover:text-amber-600">Continue Shopping →</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <nav className="flex items-center text-xs text-slate-500 mb-4">
        <Link to="/" className="hover:text-[#0b3d75]">Home</Link>
        <ChevronRight className="w-3 h-3 mx-1" />
        <Link to="/cart" className="hover:text-[#0b3d75]">Cart</Link>
        <ChevronRight className="w-3 h-3 mx-1" />
        <span className="text-slate-900 font-medium">Checkout</span>
      </nav>

      <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-6">Checkout</h1>

      <form onSubmit={placeOrder} className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="font-bold text-lg text-slate-900">Contact Information</h2>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <Field label="Full Name" id="full_name" required>
                <Input id="full_name" name="full_name" value={form.full_name} onChange={handleChange} required />
              </Field>
              <Field label="Email" id="email" required>
                <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} required />
              </Field>
              <Field label="Phone" id="phone" required className="md:col-span-2">
                <Input id="phone" name="phone" value={form.phone} onChange={handleChange} placeholder="(555) 555-1234" required />
              </Field>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="font-bold text-lg text-slate-900">Shipping Address</h2>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <Field label="Address Line 1" id="address_line1" required className="md:col-span-2">
                <Input id="address_line1" name="address_line1" value={form.address_line1} onChange={handleChange} placeholder="Street address" required />
              </Field>
              <Field label="Address Line 2" id="address_line2" className="md:col-span-2">
                <Input id="address_line2" name="address_line2" value={form.address_line2} onChange={handleChange} placeholder="Apt, suite, unit (optional)" />
              </Field>
              <Field label="City" id="city" required>
                <Input id="city" name="city" value={form.city} onChange={handleChange} required />
              </Field>
              <Field label="State" id="state" required>
                <Input id="state" name="state" value={form.state} onChange={handleChange} required maxLength={2} />
              </Field>
              <Field label="ZIP Code" id="zip_code" required>
                <Input id="zip_code" name="zip_code" value={form.zip_code} onChange={handleChange} required />
              </Field>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="font-bold text-lg text-slate-900">Order Notes (optional)</h2>
            <Textarea name="notes" value={form.notes} onChange={handleChange} placeholder="Special delivery instructions..." className="mt-3" rows={3} />
          </div>

          <div className="p-4 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-700 flex items-start gap-3">
            <Lock className="w-4 h-4 mt-0.5 text-[#0b3d75]" />
            <div>
              <div className="font-bold text-slate-900">Secure checkout</div>
              <div>Your payment will be processed securely with Square after you confirm your order.</div>
            </div>
          </div>
        </div>

        <aside className="h-fit sticky top-24 p-5 bg-white border border-slate-200 rounded-lg">
          <h3 className="font-black text-lg text-slate-900">Order Summary</h3>
          <div className="mt-4 space-y-3 max-h-72 overflow-y-auto pr-1">
            {items.map((it) => (
              <div key={it.product_id} className="flex gap-3 text-sm">
                <div className="w-14 h-14 rounded-md bg-slate-50 border overflow-hidden shrink-0 relative">
                  <img src={it.image} alt="" className="w-full h-full object-contain" />
                  <span className="absolute -top-1 -right-1 bg-[#0b3d75] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">{it.quantity}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 line-clamp-2 text-xs">{it.name}</div>
                </div>
                <div className="font-semibold text-sm">{formatMoney(it.price * it.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 my-4" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-semibold">{formatMoney(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Tax (6%)</span><span className="font-semibold">{formatMoney(tax)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Shipping</span><span className="font-semibold">{shipping === 0 ? "FREE" : formatMoney(shipping)}</span></div>
          </div>
          <div className="border-t border-slate-200 my-4" />
          <div className="flex justify-between items-baseline">
            <span className="font-black text-lg">Total</span>
            <span className="text-2xl font-black text-[#0b3d75]">{formatMoney(total)}</span>
          </div>
          <Button type="submit" disabled={placing} className="w-full mt-5 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold h-12 disabled:opacity-70">
            {placing ? "Placing order..." : "Place Order & Continue to Payment"}
          </Button>
          <p className="text-xs text-slate-500 mt-3 text-center">You'll pay with Square on the next step.</p>
        </aside>
      </form>
    </div>
  );
};

export default Checkout;
